import { Box, Flex, Grid, VStack } from '@chakra-ui/react';
import { DeliveryOrder } from '@diamond/shared/types';
import { Text } from '@diamond/shared/ui';
import { formatShortDate } from '@diamond/shared/utils';
import { DO_STATUS_CONFIG } from '@diamond/sol-buyer/constant';

import { PodReasonsSection } from './PodReasonsSection';
import { ProductItem } from './ProductItem';
import { StorageIcon } from './StorageIcon';
import { OrderItemQuantityMetaMap } from './types';

export interface DeliveryOrderSectionProps {
  deliveryOrder: DeliveryOrder;
  index: number;
  orderItemMetaById?: OrderItemQuantityMetaMap;
}

/**
 * Displays a single DO (Delivery Order) section with:
 * - DO number and index (e.g., "Pengiriman 1", "4031644005")
 * - DO STATUS from backend (Dikemas, Dikirim, Diterima, etc.) <- DO_STATUS_CONFIG
 * - Estimated date based on DO status
 * - List of products in this DO
 * - POD rejection reasons if any
 */
export function DeliveryOrderSection({
  deliveryOrder,
  index,
  orderItemMetaById,
}: DeliveryOrderSectionProps) {
  const statusConfig = DO_STATUS_CONFIG[deliveryOrder.status];
  const estimatedDate = formatShortDate(deliveryOrder.do_delivered_at);

  return (
    <Box py={2} data-testid="delivery-order-section">
      {/* DO Header - 3 columns: Pengiriman | Estimasi | Status */}
      <Grid templateColumns="1fr 90px 90px" gap={2} mb={2}>
        {/* Column 1: Pengiriman */}
        <Box>
          <Text fontSize="10px" color="gray.500">
            Pengiriman {index + 1}
          </Text>
          <Flex align="center" gap={1} mt={0.5}>
            <Text fontSize="sm" fontWeight="medium" color="gray.800">
              {deliveryOrder.do_number || '-'}
            </Text>
            {deliveryOrder.items[0]?.product_storage && (
              <StorageIcon
                storageType={deliveryOrder.items[0].product_storage}
              />
            )}
          </Flex>
        </Box>

        {/* Column 2: Estimasi */}
        <Box textAlign="center">
          <Text
            fontFamily="Libre Franklin"
            fontSize="10px"
            fontWeight={400}
            lineHeight="15px"
            letterSpacing="0%"
            color="#AEAEB2"
          >
            Estimasi
          </Text>
          <Text
            fontFamily="Libre Franklin"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
            letterSpacing="0%"
            color="#373F41"
            mt={0.5}
          >
            {estimatedDate}
          </Text>
        </Box>

        {/* Column 3: Status */}
        <Box textAlign="right">
          <Text fontSize="10px" color="gray.500" mb={0.5}>
            Status
          </Text>
          <Flex align="center" gap={1} justify="flex-end">
            <Box
              w="6px"
              h="6px"
              borderRadius="full"
              bg={statusConfig.dotColor}
            />
            <Text fontSize="xs" fontWeight="medium" color={statusConfig.color}>
              {statusConfig.label}
            </Text>
          </Flex>
        </Box>
      </Grid>

      {/* Product List */}
      <VStack align="stretch" spacing={0} pl={0}>
        {deliveryOrder.items.map((item) => (
          <ProductItem
            key={item.id}
            item={item}
            quantityMeta={orderItemMetaById?.[item.order_item_id]}
          />
        ))}
      </VStack>

      {/* POD Reasons - grouped by reason */}
      <PodReasonsSection items={deliveryOrder.items} />
    </Box>
  );
}
